import React, { useState } from "react";
import {
  MdAddCircleOutline,
  MdOutlineAddLocationAlt,
  MdOutlineArrowCircleLeft,
  MdOutlineArrowCircleRight,
} from "react-icons/md";
import LocationCardForm from "./cards/LocationCardForm";
import { LocationListType } from "utils/interfaces";
import { AnimatePresence } from "framer-motion";
import AddLocationPanel from "components/main-view/utils/pop-up-panels/add-location-pop-up";
import { LoadingDots } from "utils/loadingAnimation";

interface LocationDetailsProps {
  locations: LocationListType[];
  setLocations: React.Dispatch<React.SetStateAction<LocationListType[]>>;
  setStage: React.Dispatch<React.SetStateAction<number>>;
  activateSaveButton: boolean;
  isSaving: boolean;
  handleSave: () => Promise<void>;
}

const LocationDetails: React.FC<LocationDetailsProps> = ({
  locations,
  setLocations,
  setStage,
  activateSaveButton,
  isSaving,
  handleSave,
}) => {
  // States definition
  const [showAddLocationPopUp, setShowAddLocationPopUp] = useState(false);

  /**
   * Define handle previous button
   */
  const handlePrev = () => {
    setStage(1);
  };

  /**
   * Define handle next button
   */
  const handleNext = () => {
    setStage(3);
  };

  /**
   * Function that handle the add location pop up
   */
  const handleAddLocation = () => {
    setShowAddLocationPopUp(true);
  };

  /**
   * Function to handle the delete action
   */
  const deleteAction = (index: number) => {
    setLocations([...locations.slice(0, index), ...locations.slice(index + 1)]);
  };

  return (
    <div className="w-[562px] h-full p-6 rounded-3xl justify-start items-start gap-2 inline-flex">
      <div className="w-full flex-col justify-start items-start gap-12 inline-flex">
        <div className="self-stretch h-7 flex-col justify-start items-start gap-12 flex">
          <div className="self-stretch text-neutral-700 text-[23px] font-semibold font-sans leading-7">
            Location details
          </div>
        </div>
        {locations.length === 0 && (
          <button
            className="self-stretch h-12 flex-col justify-start items-start gap-12 flex cursor-pointer"
            onClick={handleAddLocation}
            title="Add a location"
          >
            <div className="w-full px-2 py-3 rounded-lg border border-dashed border-neutral-300 hover:bg-neutral-25 ease-in-out duration-150 justify-center items-center gap-2 inline-flex">
              <div className="w-6 h-6 relative">
                <MdOutlineAddLocationAlt className="w-full h-full fill-neutral-500" />
              </div>
              <div className="text-neutral-450 text-base font-normal font-sans leading-[19px]">
                Add a location
              </div>
            </div>
          </button>
        )}
        {locations.length > 0 && (
          <div className="self-stretch h-fit flex-col justify-start items-start gap-5 flex">
            {locations.map((location: LocationListType, index: number) => (
              <LocationCardForm
                key={index}
                index={index}
                location={location}
                deleteAction={deleteAction}
              />
            ))}
            <button
              className="group w-fit rounded justify-start items-center gap-2 flex cursor-pointer"
              onClick={handleAddLocation}
            >
              <div className="w-6 h-6 relative">
                <MdAddCircleOutline className="w-full h-full fill-secondary-600 group-hover:fill-secondary-700" />
              </div>
              <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                Add additional location
              </div>
            </button>
          </div>
        )}
        <div className="h-[46px] flex flex-row justify-between items-center w-full">
          <button
            className="group flex rounded justify-center items-center gap-2 cursor-pointer"
            onClick={handlePrev}
          >
            <div className="w-6 h-6 relative">
              <MdOutlineArrowCircleLeft className="w-full h-full fill-secondary-600 group-hover:fill-secondary-700" />
            </div>
            <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans">
              Prev
            </div>
          </button>
          {/* <button
            className="group flex rounded justify-center items-center gap-2 cursor-pointer"
            onClick={handleNext}
          >
            <div
              className={
                true
                  ? "text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans"
                  : "text-neutral-200 text-lg font-semibold font-sans"
              }
            >
              Next
            </div>
            <div className="w-6 h-6 relative">
              <MdOutlineArrowCircleRight
                className={
                  true
                    ? "w-full h-full fill-secondary-600 group-hover:fill-secondary-700"
                    : "w-full h-full fill-neutral-200"
                }
              />
            </div>
          </button> */}
          <button
            className={`w-[219px] h-full px-5 py-3 rounded justify-center items-center gap-2 flex ${
              activateSaveButton
                ? " bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 cursor-pointer"
                : " bg-Subtle rounded justify-center items-center gap-2 flex cursor-not-allowed"
            }`}
            onClick={handleSave}
          >
            {isSaving ? (
              <LoadingDots bgColor="bg-neutral-400" />
            ) : (
              <div
                className={
                  activateSaveButton
                    ? "text-white text-lg font-semibold font-sans leading-snug"
                    : "text-neutral-400 text-lg font-semibold font-sans leading-snug"
                }
              >
                Save and create
              </div>
            )}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {/* Add location pop-up */}
        {showAddLocationPopUp && (
          <AddLocationPanel
            setLocations={setLocations}
            setShowPopUp={setShowAddLocationPopUp}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LocationDetails;
